import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/contexts/confirmContext';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { formatCampaignMoney } from '../components/ContactAgentMetricCard.utils';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentRevokeOptOutDialog } from '../components/ContactAgentRevokeOptOutDialog';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';
import type { ContactRecord, DebtRecord, ContactAgent } from '@workspace/types';
import { showToast } from '@/components/ui/toast.utils';
import { copyTextToClipboard } from '@/lib/utils';

const PAGE_SIZE = 50;

function contactLabel(contact: ContactRecord): string {
  return (
    contact.businessName ||
    contact.firstName ||
    contact.phoneE164 ||
    contact.email ||
    contact.id
  );
}

function debtSummary(debts: DebtRecord[] | undefined): string {
  if (!debts || debts.length === 0) {
    return '—';
  }
  return debts
    .slice(0, 3)
    .map(debt => `${debt.invoiceNumber} (${formatCampaignMoney(debt.balance)})`)
    .join(', ');
}

function formatOptOutAt(value: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

export function ContactAgentContactsPage() {
  const { confirm } = useConfirm();
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [items, setItems] = useState<ContactRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [optOutOnly, setOptOutOnly] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ContactRecord | null>(null);
  const [cobranzasAgents, setCobranzasAgents] = useState<ContactAgent[]>([]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    void contactAgentApi.listAgents().then(agents => {
      setCobranzasAgents(agents.filter(agent => agent.domain === 'COBRANZAS'));
    });
  }, [ready, tenantId]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void contactAgentApi
      .listContacts(PAGE_SIZE, offset, optOutOnly ? { optOut: true } : undefined)
      .then(result => {
        if (!cancelled) {
          setItems(result.items);
          setTotal(result.total);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId, offset, optOutOnly]);

  const optOut = async (contact: ContactRecord) => {
    const accepted = await confirm({
      title: 'Dar de baja',
      message: `Se deja de contactar a ${contactLabel(contact)} (${contact.phoneE164}). Cancela campañas y reintentos de este contacto.`,
      confirmText: 'Dar de baja',
      variant: 'danger',
    });
    if (!accepted) {
      return;
    }
    setBusyId(contact.id);
    try {
      const updated = await contactAgentApi.optOutContact(contact.id, 'admin');
      setItems(current => current.map(item => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setBusyId('');
    }
  };

  const revokeOptOut = async (reason: string) => {
    if (!revokeTarget) {
      return;
    }
    setBusyId(revokeTarget.id);
    try {
      const updated = await contactAgentApi.revokeContactOptOut(revokeTarget.id, reason);
      setItems(current => current.map(item => (item.id === updated.id ? updated : item)));
      setRevokeTarget(null);
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setBusyId('');
    }
  };

  const setFilter = (onlyOptOut: boolean) => {
    setOptOutOnly(onlyOptOut);
    setOffset(0);
  };

  const copyAccessLink = async (contact: ContactRecord) => {
    if (cobranzasAgents.length === 0) {
      showToast('No hay agente de cobranzas configurado', 'error');
      return;
    }
    const agent = cobranzasAgents[0];
    setBusyId(contact.id);
    try {
      const { url } = await contactAgentApi.getContactAccessLink(contact.id, agent.id);
      await copyTextToClipboard(url);
      showToast('Link de acceso copiado', 'success');
    } catch (err) {
      showToast(contactAgentError(err), 'error');
    } finally {
      setBusyId('');
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Contactos'
        description='Cartera importada, deudas pendientes, bajas y revocación si el cliente autorizó por otro canal.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-3'>
          <CardTitle className='text-lg'>Listado</CardTitle>
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              size='sm'
              variant={optOutOnly ? 'outline' : 'secondary'}
              onClick={() => setFilter(false)}
            >
              Todos
            </Button>
            <Button
              type='button'
              size='sm'
              variant={optOutOnly ? 'secondary' : 'outline'}
              onClick={() => setFilter(true)}
            >
              Solo bajas
            </Button>
            <span className='text-sm text-muted-foreground'>{total} contactos</span>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b text-left text-muted-foreground'>
                  <th className='py-2 pr-4'>Contacto</th>
                  <th className='py-2 pr-4'>Telefono</th>
                  <th className='py-2 pr-4'>Deudas</th>
                  <th className='py-2 pr-4'>Estado</th>
                  <th className='py-2'>Accion</th>
                </tr>
              </thead>
              <tbody>
                {items.map(contact => (
                  <tr key={contact.id} className='border-b last:border-0 align-top'>
                    <td className='py-3 pr-4'>
                      <Link
                        to={`/admin/contact-agent/contacts/${contact.id}`}
                        className='font-medium text-primary hover:underline'
                      >
                        {contactLabel(contact)}
                      </Link>
                      {contact.email ? (
                        <p className='text-xs text-muted-foreground'>{contact.email}</p>
                      ) : null}
                    </td>
                    <td className='py-3 pr-4 font-mono'>{contact.phoneE164 ?? '—'}</td>
                    <td className='py-3 pr-4'>
                      {debtSummary(contact.debts)}
                      {(contact.debts?.length ?? 0) > 3 ? (
                        <p className='text-xs text-muted-foreground'>
                          +{(contact.debts?.length ?? 0) - 3} mas
                        </p>
                      ) : null}
                    </td>
                    <td className='py-3 pr-4'>
                      {contact.optOut ? (
                        <div className='space-y-1'>
                          <Badge variant='destructive'>Baja</Badge>
                          {contact.optOutReason ? (
                            <p className='text-xs text-muted-foreground'>{contact.optOutReason}</p>
                          ) : null}
                          {contact.optOutAt ? (
                            <p className='text-xs text-muted-foreground'>{formatOptOutAt(contact.optOutAt)}</p>
                          ) : null}
                        </div>
                      ) : (
                        <Badge variant='secondary'>Activo</Badge>
                      )}
                    </td>
                    <td className='py-3'>
                      <div className='flex flex-col gap-2'>
                        {!contact.optOut ? (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            disabled={busyId === contact.id}
                            onClick={() => void copyAccessLink(contact)}
                          >
                            Copiar link de acceso
                          </Button>
                        ) : null}
                        {contact.optOut ? (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            disabled={busyId === contact.id}
                            onClick={() => setRevokeTarget(contact)}
                          >
                            Revocar baja
                          </Button>
                        ) : (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            disabled={busyId === contact.id}
                            onClick={() => void optOut(contact)}
                          >
                            Dar de baja
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='py-8 text-center text-muted-foreground'>
                      {optOutOnly ? 'No hay contactos dados de baja.' : 'No hay contactos cargados.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className='flex items-center justify-between'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={offset === 0}
              onClick={() => setOffset(current => Math.max(0, current - PAGE_SIZE))}
            >
              Anterior
            </Button>
            <span className='text-sm text-muted-foreground'>
              Pagina {page} de {totalPages}
            </span>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(current => current + PAGE_SIZE)}
            >
              Siguiente
            </Button>
          </div>
        </CardContent>
      </Card>
      <ContactAgentRevokeOptOutDialog
        contact={revokeTarget}
        busy={busyId === revokeTarget?.id}
        onClose={() => setRevokeTarget(null)}
        onConfirm={reason => void revokeOptOut(reason)}
      />
    </ContactAgentPage>
  );
}
